import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search } from "lucide-react";

export default function ComplianceMatrix() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/framework/matrix");
      setRows(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.topic, r.source_control_code, r.target_control_code, r.relationship]
        .some((x) => String(x || "").toLowerCase().includes(q))
    );
  }, [rows, search]);

  const grouped = useMemo(() => {
    const map = {};
    for (const r of filtered) {
      const key = `${r.topic}|${r.source_control_code}`;
      if (!map[key]) {
        map[key] = {
          topic: r.topic,
          nist: r.source_control_code,
          iso: "",
          soc: "",
          relationship: r.relationship,
        };
      }
      if (r.target_framework_code === "ISO_27001_2022") map[key].iso = r.target_control_code;
      if (r.target_framework_code === "SOC_2") map[key].soc = r.target_control_code;
    }
    return Object.values(map);
  }, [filtered]);

  return (
    <div data-testid="compliance-matrix-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="label-uppercase">Unified Control Mapping</div>
          <h1 className="text-3xl tracking-tight font-medium mt-2 text-white">Compliance Matrix</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Assess once, map controls across NIST CSF 2.0, ISO 27001, and SOC 2.
          </p>
        </div>
        <div className="text-xs text-zinc-500 mono">{grouped.length} mapped topics</div>
      </div>

      <div className="mt-6 relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search mappings…"
          className="pl-10 bg-[#121212] border-zinc-800"
          data-testid="matrix-search"
        />
      </div>

      <div className="mt-5 rounded-md border border-zinc-800 overflow-hidden bg-[#121212]">
        <table className="w-full text-sm">
          <thead className="bg-[#0A0A0A] border-b border-zinc-800">
            <tr className="text-left text-xs text-zinc-500">
              <th className="px-4 py-3 font-medium">Control Topic</th>
              <th className="px-4 py-3 font-medium">NIST CSF 2.0</th>
              <th className="px-4 py-3 font-medium">ISO 27001</th>
              <th className="px-4 py-3 font-medium">SOC 2</th>
              <th className="px-4 py-3 font-medium">Relationship</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {loading ? (
              <tr><td colSpan="5" className="px-4 py-6 text-zinc-500">Loading matrix…</td></tr>
            ) : grouped.length === 0 ? (
              <tr><td colSpan="5" className="px-4 py-6 text-zinc-500">No mappings found.</td></tr>
            ) : grouped.map((r) => (
              <tr key={`${r.topic}-${r.nist}`} className="hover:bg-zinc-900/40">
                <td className="px-4 py-3 text-zinc-300">{r.topic}</td>
                <td className="px-4 py-3"><CodeBadge value={r.nist} /></td>
                <td className="px-4 py-3"><CodeBadge value={r.iso} /></td>
                <td className="px-4 py-3"><CodeBadge value={r.soc} /></td>
                <td className="px-4 py-3">
                  <Badge className="bg-zinc-900 text-zinc-400 border-zinc-800 text-[10px] uppercase">
                    {r.relationship || "related"}
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 text-xs text-zinc-500 leading-relaxed">
        Note: Initial ISO and SOC records are seeded as configurable framework controls. Replace control statements with your approved/licensed framework wording before formal audit use.
      </div>
    </div>
  );
}

function CodeBadge({ value }) {
  if (!value) return <span className="text-zinc-700">—</span>;
  return <Badge className="bg-white/5 text-white border-zinc-700 mono">{value}</Badge>;
}
